/**
 * BOOK-168: the markup-inclusive ("final") companions of the search pricing fields.
 *
 * The backend ships each companion beside its base field (`totalWithMarkup` beside `total`,
 * `totalBaseWithMarkup`, `totalBeforeDiscountWithMarkup`) and leaves the base fields exactly as
 * they were. Every read here falls back to the base field when the companion is absent -- the
 * backend not deployed yet, the funnel flag on before the API flag, a payload cached before
 * either, a trip without a markup rule -- so the final arm can never paint `undefined` or `NaN`;
 * the worst case is control's figure.
 *
 * Which arm a user gets is decided in `utils/trips/finalPriceExperiment`. `finalPrice` serves
 * both arms, and its `isFinal` defaults to `false` on purpose, like `displayedPrice`'s `netted`: a
 * caller that forgets it paints control. The other readers serve only the final arm; control reads
 * the base field directly.
 */

/**
 * Whether a value can be painted as a price. A companion that is not a number is treated as
 * absent rather than rendered, because it would format as `NaN`.
 *
 * @param {*} value - A price field as the payload carries it (number or numeric string).
 * @returns {boolean} Whether it is a finite number.
 */
export const isPrice = (value) => value != null && value !== '' && Number.isFinite(Number(value));

/**
 * `companion ?? base`, where a non-numeric companion also counts as missing. `??` and not `||`:
 * a companion of 0 is a figure, and truthiness would paint the base exactly there.
 *
 * @param {*} companion - The markup-inclusive field.
 * @param {*} base - The base field it replaces.
 * @returns {*} The companion when it is a price, otherwise the base field as sent.
 */
const companionOr = (companion, base) => (isPrice(companion) ? companion : base);

/**
 * A finite number for sums: anything missing or non-numeric counts as 0.
 *
 * @param {*} value - A price field.
 * @returns {number} The value as a number, or 0.
 */
const toAmount = (value) => (isPrice(value) ? Number(value) : 0);

/**
 * The total to paint for a pricing object in the given arm.
 *
 * @param {{ total?: number, totalWithMarkup?: number }} [pricing] - A search pricing object, a
 *   provider-discount entry or a passenger-type entry.
 * @param {boolean} [isFinal] - Whether this user is in the final arm.
 * @returns {number|string|undefined} `totalWithMarkup` in the final arm when present, otherwise
 *   `total`.
 */
export const finalPrice = (pricing, isFinal = false) => {
  if (!isFinal) return pricing?.total;

  return companionOr(pricing?.totalWithMarkup, pricing?.total);
};

/**
 * The base total (`totalBase`, shown to logged-in and exchange users) to paint in the final arm.
 *
 * @param {{ totalBase?: number, totalBaseWithMarkup?: number }} [pricing] - A search pricing
 *   object.
 * @returns {number|string|undefined} `totalBaseWithMarkup` when present, otherwise `totalBase`.
 */
export const finalTotalBase = (pricing) =>
  companionOr(pricing?.totalBaseWithMarkup, pricing?.totalBase);

/**
 * The pre-discount (strikethrough) total to paint in the final arm.
 *
 * @param {{ totalBeforeDiscount?: number, totalBeforeDiscountWithMarkup?: number }} [pricing] -
 *   A search pricing object.
 * @returns {number|string|undefined} `totalBeforeDiscountWithMarkup` when present, otherwise
 *   `totalBeforeDiscount`.
 */
export const finalTotalBeforeDiscount = (pricing) =>
  companionOr(pricing?.totalBeforeDiscountWithMarkup, pricing?.totalBeforeDiscount);

/**
 * Whether the pricing a surface renders would look different in the final arm: a companion
 * strictly above its base total.
 *
 * This is the payload gate on evaluating the experiment, the counterpart of INS-1288's
 * `hasDisplayTotal`. Presence alone is not enough: a zero-markup rule or an inverted payload
 * paints the same figure in both arms, and bucketing those users would only dilute the readout.
 *
 * @param {{ total?: number, totalWithMarkup?: number }} [pricing] - The pricing object the
 *   surface renders.
 * @returns {boolean} Whether `totalWithMarkup` is above `total`.
 */
export const hasMarkupAboveTotal = (pricing) =>
  isPrice(pricing?.total) &&
  isPrice(pricing?.totalWithMarkup) &&
  Number(pricing.totalWithMarkup) > Number(pricing.total);

/**
 * The strikethrough total of a result card in the final arm, rebuilt when the payload carries
 * none. Control keeps the list's own `totalBeforeDiscount || amount + total`.
 *
 * The badge beside it advertises `formatPercent(totalBeforeDiscount, total)` against the painted
 * total, so the strikethrough has to sit above that total. When the pre-discount field is
 * missing, the value is rebuilt by adding the discount back to the **painted** total: against a
 * card painting base + markup, a base + discount strikethrough inverts as soon as the markup
 * exceeds the discount.
 *
 * The rebuild is deliberately approximate. The exact value is the pre-discount fare plus the
 * markup computed on it, which is larger, so it undershoots -- but it can never fall below the
 * painted total, which is the invariant that matters.
 *
 * @param {object} [pricing] - The leg pricing carrying the pre-discount fields.
 * @param {object} [displayedPricing] - The pricing object the card paints its total from.
 * @param {{ amount?: number }} [providerDiscount] - The applied provider discount.
 * @returns {number} The total to strike through; 0 when nothing can be derived, never `NaN`.
 */
export const resolveStrikethroughTotal = (pricing, displayedPricing, providerDiscount) =>
  toAmount(finalTotalBeforeDiscount(pricing)) ||
  toAmount(providerDiscount?.amount) + toAmount(finalPrice(displayedPricing, true));

/**
 * The markup between a base value and its companion.
 *
 * Each fare carries its own base, and a percent rule applies to that base, so the markup of a
 * discounted category is not the general fare's. Callers that assign several fares in one
 * purchase resolve each one here, against the pair of fields that fare was priced from.
 *
 * @param {number|string} [baseValue] - The fare's base value.
 * @param {number|string} [finalValue] - The fare's markup-inclusive value.
 * @returns {number} The markup, never negative; 0 when either value is missing or not a number.
 */
export const markupDeltaBetween = (baseValue, finalValue) => {
  if (!isPrice(baseValue) || !isPrice(finalValue)) return 0;

  return Math.max(Number(finalValue) - Number(baseValue), 0);
};

/**
 * The markup contained in a pricing object.
 *
 * Prefers the explicit `markup` field and falls back to `totalWithMarkup - total`, so it works on
 * payloads that carry the companion but not the amount. A negative explicit markup is clamped
 * to 0 rather than differenced: a negative fee would render as a discount.
 *
 * @param {{ markup?: number, total?: number, totalWithMarkup?: number }} [pricing] - A search
 *   pricing object.
 * @returns {number} The markup, never negative and never `NaN`.
 */
export const markupDelta = (pricing) => {
  const explicit = toAmount(pricing?.markup);
  if (explicit) return Math.max(explicit, 0);

  return markupDeltaBetween(pricing?.total, pricing?.totalWithMarkup);
};
